import {
	Alert,
	AlertDescription,
	Badge,
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Input,
	Label,
	Skeleton,
} from "@ancatag/at-design";
import {
	CheckCircle2,
	CreditCard,
	Loader2,
	Shield,
	XCircle,
} from "lucide-react";
import { useEffect, useState } from "react";
import { cn } from "../../../lib/utils";
import type { ProviderTemplate } from "../../../services/provider-templates";

interface Step3NovaHostedProps {
	provider: ProviderTemplate;
	onModelSelect: (modelId: string) => void;
	selectedModelId?: string;
}

interface NovaHostedModel {
	id: string;
	name: string;
	description?: string;
	contextWindow?: number;
	capabilities?: {
		streaming?: boolean;
		functionCalling?: boolean;
		vision?: boolean;
	};
	pricing: {
		inputPer1K: number;
		outputPer1K: number;
	};
	isRecommended?: boolean;
}

export function Step3NovaHosted({
	provider,
	onModelSelect,
	selectedModelId,
}: Step3NovaHostedProps) {
	const [models, setModels] = useState<NovaHostedModel[]>([]);
	const [loading, setLoading] = useState(true);
	const [testing, setTesting] = useState(false);
	const [testResult, setTestResult] = useState<{
		success: boolean;
		message: string;
	} | null>(null);
	const [estimatedTokens, setEstimatedTokens] = useState(1000);
	const [billingStatus, setBillingStatus] = useState<{
		credits: number;
		lowCredits: boolean;
	} | null>(null);

	// Mock data for now - will be replaced with API call
	useEffect(() => {
		setTimeout(() => {
			setModels([
				{
					id: "gpt-4-turbo",
					name: "GPT-4 Turbo",
					description: "Most capable model for complex tasks",
					contextWindow: 128000,
					capabilities: {
						streaming: true,
						functionCalling: true,
						vision: true,
					},
					pricing: {
						inputPer1K: 0.01,
						outputPer1K: 0.03,
					},
					isRecommended: true,
				},
				{
					id: "gpt-3.5-turbo",
					name: "GPT-3.5 Turbo",
					description: "Fast and efficient for most tasks",
					contextWindow: 16385,
					capabilities: {
						streaming: true,
						functionCalling: true,
					},
					pricing: {
						inputPer1K: 0.0005,
						outputPer1K: 0.0015,
					},
				},
			]);
			setBillingStatus({
				credits: 100.0,
				lowCredits: false,
			});
			setLoading(false);
		}, 500);
	}, []);

	const handleTestConnection = async () => {
		setTesting(true);
		setTestResult(null);

		try {
			// Mock connection test
			await new Promise((resolve) => setTimeout(resolve, 1000));
			setTestResult({
				success: true,
				message: "Connection successful. Nova-hosted endpoint is available.",
			});
		} catch (error: any) {
			setTestResult({
				success: false,
				message: error.message || "Connection test failed",
			});
		} finally {
			setTesting(false);
		}
	};

	const calculateCost = (tokens: number, model: NovaHostedModel): number => {
		// Simple calculation: assume 50% input, 50% output
		const inputCost = (tokens / 2 / 1000) * model.pricing.inputPer1K;
		const outputCost = (tokens / 2 / 1000) * model.pricing.outputPer1K;
		return inputCost + outputCost;
	};

	if (loading) {
		return (
			<div className="space-y-4">
				<Skeleton className="h-32 w-full" />
				<Skeleton className="h-64 w-full" />
			</div>
		);
	}

	return (
		<div className="space-y-6">
			{/* Welcome Card */}
			<Card className="border-primary/20 bg-primary/5">
				<CardHeader>
					<div className="flex items-center gap-3">
						<Shield className="h-5 w-5 text-primary" />
						<div>
							<CardTitle>Nova-Managed Infrastructure</CardTitle>
							<CardDescription>
								Nova manages all credentials and infrastructure. No API keys
								needed - just select your model.
							</CardDescription>
						</div>
					</div>
				</CardHeader>
			</Card>

			{/* Billing Status */}
			{billingStatus && (
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<CreditCard className="h-4 w-4" />
							Billing Status
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="flex items-center justify-between">
							<div>
								<p className="text-sm font-medium">Available Credits</p>
								<p className="text-2xl font-bold">
									${billingStatus.credits.toFixed(2)}
								</p>
							</div>
							<div className="flex gap-2">
								<Button variant="outline" size="sm">
									View Billing
								</Button>
								<Button variant="default" size="sm">
									Add Credits
								</Button>
							</div>
						</div>
						{billingStatus.lowCredits && (
							<Alert className="mt-4 border-yellow-500">
								<AlertDescription>
									Low credits remaining. Please add credits to continue using
									Nova-hosted models.
								</AlertDescription>
							</Alert>
						)}
					</CardContent>
				</Card>
			)}

			{/* Model Selection */}
			<Card>
				<CardHeader>
					<CardTitle>Select Model</CardTitle>
					<CardDescription>
						Choose a Nova-hosted model for {provider.name}
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						{models.map((model) => (
							<Card
								key={model.id}
								className={cn(
									"cursor-pointer transition-all hover:shadow-md",
									selectedModelId === model.id && "ring-2 ring-primary",
								)}
								onClick={() => onModelSelect(model.id)}
							>
								<CardHeader>
									<div className="flex items-start justify-between">
										<div className="flex-1">
											<div className="flex items-center gap-2">
												<CardTitle className="text-base">
													{model.name}
												</CardTitle>
												{model.isRecommended && (
													<Badge variant="default" className="text-xs">
														Recommended
													</Badge>
												)}
											</div>
											{model.description && (
												<CardDescription className="mt-1">
													{model.description}
												</CardDescription>
											)}
										</div>
									</div>
								</CardHeader>
								<CardContent className="space-y-3">
									{model.contextWindow && (
										<div className="text-sm text-muted-foreground">
											Context: {model.contextWindow.toLocaleString()} tokens
										</div>
									)}
									<div className="flex flex-wrap gap-2">
										{model.capabilities?.streaming && (
											<Badge variant="outline" className="text-xs">
												Streaming
											</Badge>
										)}
										{model.capabilities?.functionCalling && (
											<Badge variant="outline" className="text-xs">
												Functions
											</Badge>
										)}
										{model.capabilities?.vision && (
											<Badge variant="outline" className="text-xs">
												Vision
											</Badge>
										)}
									</div>
									<div className="pt-2 border-t">
										<p className="text-sm font-medium">Pricing</p>
										<p className="text-xs text-muted-foreground">
											${model.pricing.inputPer1K.toFixed(4)} / 1K input, $
											{model.pricing.outputPer1K.toFixed(4)} / 1K output
										</p>
									</div>
								</CardContent>
							</Card>
						))}
					</div>
				</CardContent>
			</Card>

			{/* Pricing Calculator */}
			<Card>
				<CardHeader>
					<CardTitle>Cost Calculator</CardTitle>
					<CardDescription>
						Estimate costs based on your expected usage
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="space-y-2">
						<Label htmlFor="estimatedTokens">
							Estimated Tokens per Request
						</Label>
						<Input
							id="estimatedTokens"
							type="number"
							min="1"
							value={estimatedTokens}
							onChange={(e) =>
								setEstimatedTokens(Number.parseInt(e.target.value, 10) || 0)
							}
						/>
					</div>
					{selectedModelId && (
						<div className="space-y-2">
							{models
								.filter((m) => m.id === selectedModelId)
								.map((model) => (
									<div key={model.id} className="p-4 bg-muted rounded-lg">
										<p className="text-sm font-medium mb-2">
											Estimated Cost per Request
										</p>
										<p className="text-2xl font-bold">
											${calculateCost(estimatedTokens, model).toFixed(6)}
										</p>
										<p className="text-xs text-muted-foreground mt-1">
											Based on {estimatedTokens.toLocaleString()} tokens (50%
											input, 50% output)
										</p>
									</div>
								))}
						</div>
					)}
				</CardContent>
			</Card>

			{/* Connection Test */}
			<Card>
				<CardHeader>
					<CardTitle>Connection Test</CardTitle>
					<CardDescription>
						Test connection to Nova-hosted endpoint
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					<Button
						onClick={handleTestConnection}
						disabled={testing}
						className="w-full h-10"
					>
						{testing ? (
							<>
								<Loader2 className="h-4 w-4 mr-2 animate-spin" />
								Testing Connection...
							</>
						) : (
							"Test Connection"
						)}
					</Button>

					{testResult && (
						<Alert
							className={
								testResult.success ? "border-green-500" : "border-red-500"
							}
						>
							<div className="flex items-center gap-2">
								{testResult.success ? (
									<CheckCircle2 className="h-4 w-4 text-green-500" />
								) : (
									<XCircle className="h-4 w-4 text-red-500" />
								)}
								<AlertDescription>{testResult.message}</AlertDescription>
							</div>
						</Alert>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
